import { useIntl } from "react-intl";
import React, { useCallback, useState } from "react";

// Components
import { Box, Button, Divider, InputAdornment, TextField, Typography } from "@mui/material";
import CustomIcon from "components/CustomIcon";
import { DataList } from "../DataList";
import { SelectedDataList } from "../SelectedDataList";

// Enums
import icons from "enums/icons";

// Utils
import { differenceBy, keyBy, merge, values } from "lodash";

// Styles
import { Theme } from "@mui/material/styles";

// Types
import { TTeam, TTeamsData } from "../../BehaviouralComparison.types";
import { TList } from "pages/BehavioralComparison/useBehaviouralComparison";
import { BCIndividualsRefResponse, BCTeamsRefResponse, getPaceDashboardTeamsRef } from "services";
import { useAuth, useInputDebounce } from "utils";

const TeamsFilter = ({
    data,
    setData,
    handleSetResults,
    handleOpenTeamsModal,
    pagination,
}: {
    data: TTeamsData;
    setData: (data: TTeamsData, avoidFetching?: boolean) => void;
    handleSetResults: <T extends BCIndividualsRefResponse | BCTeamsRefResponse>(data: T, type: TList) => void;
    handleOpenTeamsModal: (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    pagination: React.ReactNode;
}) => {
    const intl = useIntl();

    const { loggedInUserData } = useAuth();
    const orgId = loggedInUserData.selectedOrg?.id;

    const [inputTeamValue, setInputTeamValue] = useState<string>("");
    const [expandSelected, setExpandSelected] = useState<boolean>(false);

    const areSomeTeamsSelected = Boolean(
        data.data.filter((team) => team.checked).length > 0 &&
            data.data.filter((team) => team.checked).length < data.data.length
    );
    const areAllTeamsSelected = data.data.every((team) => team.checked);

    const hasResults = data.data.length > 0;
    const hasNoResults = !data.data.length && data.status === "done" && inputTeamValue.trim() !== "";
    const hasNoTeams = !data.data.length && data.status === "done" && inputTeamValue.trim() === "";

    // --------------------------------------------------------------------------------------------
    // SELECT AND UNSELECT FUNCTIONS
    // --------------------------------------------------------------------------------------------
    const handleCheck = (event: React.ChangeEvent<HTMLInputElement>, id: number) => {
        const checked = event.target.checked;
        const newData = data.data.map((team) => (team.id === id ? { ...team, checked } : team));
        const checkedTeams = newData.filter((team) => team.checked);
        let newSelectedData = [] as TTeam[];

        if (checked) {
            newSelectedData = values(merge(keyBy(data.selected || [], "id"), keyBy(checkedTeams, "id")));
        } else {
            newSelectedData = data.selected?.filter((team) => team.id !== id);
        }
        setData(
            {
                ...data,
                data: newData,
                selected: newSelectedData,
            },
            !checked
        );
    };

    const handleCheckAll = (event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;
        const newData = data.data.map((team) => ({ ...team, checked }));
        const checkedTeams = newData.filter((team) => team.checked);

        const newSelected = checked
            ? values(merge(keyBy(data.selected || [], "id"), keyBy(checkedTeams, "id")))
            : differenceBy(data.selected, newData, "id");

        setData({
            ...data,
            data: newData,
            selected: newSelected,
        });
    };

    const handleDeleteSelected = (id: number) => {
        const newData = data.data.map((team) => (team.id === id ? { ...team, checked: false } : team));
        const selectedData = data.selected.filter((team) => team.id !== id);

        setData(
            {
                ...data,
                data: newData,
                selected: selectedData,
            },
            true
        );
    };

    const handleRemoveAllSelected = () => {
        const newData = data.data.map((team) => ({ ...team, checked: false }));

        setExpandSelected(false);
        setData(
            {
                ...data,
                data: newData,
                selected: [],
            },
            true
        );
    };

    // --------------------------------------------------------------------------------------------
    // FILTER BY TEAM
    // --------------------------------------------------------------------------------------------
    const handleChangeFilters = useCallback(
        async (value: string) => {
            if (!orgId) return;
            setData({ ...data, status: "fetching" }, true);
            const results = await getPaceDashboardTeamsRef({ ...data.meta, filter_team: value, orgId });
            handleSetResults<BCTeamsRefResponse>(results.data, "teams");
        },
        [data, handleSetResults, orgId, setData]
    );

    const handleFilterByTeam = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setInputTeamValue(value);
        delayedHandleInputChange(value);
    };

    const { delayedHandleInputChange } = useInputDebounce(handleChangeFilters);

    return (
        <>
            <Box sx={{ width: "100%" }}>
                <TextField
                    fullWidth
                    size="small"
                    id="team-search"
                    name="team-search"
                    autoComplete="off"
                    disabled={hasNoTeams}
                    value={inputTeamValue}
                    sx={(theme: Theme) => ({
                        width: "100%",
                        marginTop: theme.spacing(1),
                        "& .MuiInputBase-input": {
                            height: 22,
                        },
                        "& .MuiInputAdornment-root .MuiSvgIcon-root": {
                            fontSize: 16,
                        },
                    })}
                    onChange={handleFilterByTeam}
                    placeholder={intl.formatMessage({
                        id: "bahaviouralComparison.filterByTeam",
                    })}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <CustomIcon icon={icons.searchInverted} />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            {hasNoTeams && (
                <Box sx={{ height: 260, textAlign: "center", marginTop: 2 }}>
                    <Typography sx={{ fontSize: 16, mb: 2 }}>
                        {intl.formatMessage({
                            id: "behaviouralComparison.filterByTeam.noTeamsData",
                        })}
                    </Typography>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={handleOpenTeamsModal}
                        startIcon={<CustomIcon icon={icons.hyperlink} fontSize="small" />}
                    >
                        Add Team
                    </Button>
                </Box>
            )}

            {hasNoResults && (
                <Box sx={{ height: 260, textAlign: "center", marginTop: 2 }}>
                    <Typography sx={{ fontSize: 16 }}>
                        {intl.formatMessage({
                            id: "behaviouralComparison.filterByTeam.noResults",
                        })}
                    </Typography>
                </Box>
            )}

            {hasResults && (
                <>
                    <SelectedDataList
                        type="teams"
                        selectedItems={data.selected}
                        allSelected={areAllTeamsSelected}
                        someSelected={areSomeTeamsSelected}
                        expandSelected={expandSelected}
                        setExpandSelected={setExpandSelected}
                        handleCheckAll={handleCheckAll}
                        handleRemoveAllSelected={handleRemoveAllSelected}
                        handleDeleteSelected={handleDeleteSelected}
                    />

                    <Divider sx={{ color: "neutrals.light", margin: "8px 0px 8px 0px" }} />

                    <DataList
                        id="teams-list"
                        pagination={pagination}
                        type="team"
                        filteredData={data.data}
                        handleCheck={handleCheck}
                    />
                </>
            )}
        </>
    );
};

export default TeamsFilter;
